/**
 * Version utility functions for comparing app versions
 */

// Get current app version from package.json (injected at build time)
export const getCurrentVersion = (): string => {
  // This will be replaced at build time with the actual version
  return process.env.REACT_APP_VERSION || "1.0.0";
};

/**
 * Convert version string (e.g., "1.5.0") to numeric representation (1050000)
 * @param version - Version string in format "X.Y.Z"
 * @returns Numeric representation
 */
export const versionToNumber = (version: string): number => {
  try {
    const parts = version.split(".").map(Number);
    if (parts.length !== 3) {
      throw new Error("Invalid version format");
    }
    // Use the same formula as versioning.js: major * 1000000 + minor * 10000 + patch
    // This allows for versions like 1.10.5 -> 1100005
    return parts[0] * 1000000 + parts[1] * 10000 + parts[2];
  } catch (error) {
    console.error("Error converting version to number:", error);
    return 0;
  }
};

/**
 * Convert numeric representation (e.g., 1050000) to version string (1.5.0)
 * @param versionNumber - Numeric representation
 * @returns Version string
 */
export const numberToVersion = (versionNumber: number): string => {
  try {
    if (isNaN(versionNumber) || versionNumber < 0) {
      return "0.0.0";
    }
    const major = Math.floor(versionNumber / 1000000);
    const minor = Math.floor((versionNumber % 1000000) / 10000);
    const patch = versionNumber % 10000;
    return `${major}.${minor}.${patch}`;
  } catch (error) {
    console.error("Error converting number to version:", error);
    return "0.0.0";
  }
};

/**
 * Check if current version is outdated compared to latest version
 * @param currentVersion - Current app version (e.g., "1.5.0")
 * @param latestVersionNumber - Latest version from remote config (e.g., 160)
 * @returns True if current version is outdated
 */
export const isVersionOutdated = (
  currentVersion: string,
  latestVersionNumber: number
): boolean => {
  const currentVersionNumber = versionToNumber(currentVersion);
  return currentVersionNumber < latestVersionNumber;
};

/**
 * Get formatted version comparison message
 * @param currentVersion - Current app version
 * @param latestVersionNumber - Latest version from remote config
 * @returns Formatted message for display
 */
export const getVersionUpdateMessage = (
  currentVersion: string,
  latestVersionNumber: number
): string => {
  const latestVersion = numberToVersion(latestVersionNumber);
  return `Please update to the latest version to avoid data corruption: v${currentVersion} → v${latestVersion}`;
};
